import * as React from 'react';

import { cn, createTestIdHelper } from '../../../lib/utils';

import {
  StatePlaceholderContext,
  type StatePlaceholderContextValue,
} from './state-placeholder-context';
import {
  StatePlaceholderAction,
  StatePlaceholderContent,
  StatePlaceholderDescription,
  StatePlaceholderFooter,
  StatePlaceholderIcon,
  StatePlaceholderTitle,
} from './state-placeholder-slots';
import {
  ROOT_CLASSES,
  type StatePlaceholderDensity,
} from './state-placeholder-variants';

const defaultTestId = createTestIdHelper('state-placeholder');

export interface StatePlaceholderProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'role'
> {
  /** Defaults to `panel`. Shared with every slot through context — never repeat it per slot. */
  density?: StatePlaceholderDensity;
  /**
   * Live-region semantics, written AFTER the spread so a call site cannot reach it.
   *
   * Set by the SEMANTIC component on top of this primitive, which omits `role` from its own
   * props type. `alert` interrupts the reader and claims something failed; use it only for a
   * state that genuinely is a failure.
   */
  role?: 'status' | 'alert';
  /** Test-id namespace. Semantic components pass their own so selectors stay per-component. */
  testId?: (element: string) => string;
  /**
   * Whether the root ACCEPTS programmatic focus (`tabIndex={-1}`). Defaults to true at
   * `page`.
   *
   * It only prepares the root; nothing in this component calls `focus()`, the same division
   * `role` draws between semantics and announcement. Moving focus stays with the call site,
   * which alone knows when the swap happened — a `page` state that replaces content after a
   * fetch leaves focus on a control that no longer exists, and without `tabIndex` the call
   * site has no target to move it to. Static page content that never replaces anything
   * passes `false`.
   */
  focusable?: boolean;
  children: React.ReactNode;
}

/**
 * The shared anatomy behind every "there is nothing here, and here is why" surface: an icon,
 * an optional title and description, an optional action, in one of three densities.
 *
 * Generic on purpose. It fixes no copy, no colour and no live-region role — the semantic
 * components above it (`RestrictedPlaceholder`, `NotFoundPlaceholder`,
 * `ComingSoonPlaceholder`) decide those, and callers compose the sections they need.
 *
 * @example
 * <StatePlaceholder density="page" role="status">
 *   <StatePlaceholder.Icon><Lock /></StatePlaceholder.Icon>
 *   <StatePlaceholder.Content>
 *     <StatePlaceholder.Title>No access</StatePlaceholder.Title>
 *     <StatePlaceholder.Description>Ask an administrator.</StatePlaceholder.Description>
 *   </StatePlaceholder.Content>
 *   <StatePlaceholder.Action><Button>Back</Button></StatePlaceholder.Action>
 * </StatePlaceholder>
 */
const StatePlaceholderRoot = React.forwardRef<
  HTMLDivElement,
  StatePlaceholderProps
>(
  (
    {
      density = 'panel',
      role,
      testId = defaultTestId,
      focusable,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const titleRegistered = React.useRef(false);

    const registerTitle = React.useCallback(() => {
      titleRegistered.current = true;
    }, []);

    // The density union used to make "page without a title" a compile error. A composed API
    // cannot express that, so the same mistake is caught here instead. `useEffect` dependency
    // arrays survive the React Compiler, unlike a memo's, so the ref read is safe.
    React.useEffect(() => {
      if (density !== 'page' || titleRegistered.current) {
        return;
      }

      // `import.meta.env.DEV` is statically replaced at build time, so the warning below is
      // removed from production bundles rather than shipped to a user's console.
      if (!import.meta.env.DEV) {
        return;
      }

      console.warn(
        'StatePlaceholder: a `page` density rendered without a Title. A route-scale state with no heading leaves screen-reader users nothing to navigate to.',
      );
    }, [density]);

    const value = React.useMemo<StatePlaceholderContextValue>(
      () => ({ density, testId, registerTitle }),
      [density, testId, registerTitle],
    );

    return (
      <StatePlaceholderContext.Provider value={value}>
        {/*
          Children render verbatim. This Root NEVER inspects child `type.displayName` to
          supply defaults: the semantic components above wrap these slots, and detection only
          sees DIRECT children, so a wrapped section would vanish with no typecheck, lint or
          runtime error. Add explicit slots instead of adding detection back.
        */}
        <div
          ref={ref}
          // Before the spread: consumers legitimately rename this to keep a feature selector.
          data-testid={testId('root')}
          tabIndex={(focusable ?? density === 'page') ? -1 : undefined}
          {...rest}
          role={role}
          className={cn(ROOT_CLASSES[density], className)}
        >
          {children}
        </div>
      </StatePlaceholderContext.Provider>
    );
  },
);

StatePlaceholderRoot.displayName = 'StatePlaceholder';

export const StatePlaceholder = Object.assign(StatePlaceholderRoot, {
  Icon: StatePlaceholderIcon,
  Content: StatePlaceholderContent,
  Title: StatePlaceholderTitle,
  Description: StatePlaceholderDescription,
  Action: StatePlaceholderAction,
  Footer: StatePlaceholderFooter,
});
